import React, {useState, useRef, useEffect} from 'react';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import {AppBar, Toolbar, Grid, Paper, Typography, Box, Button, TextField, Link, InputAdornment, CircularProgress} from '@mui/material';
import loginImage from '../../../assets/auth/login.jpg';
import logo from '../../../assets/dashboard/logo.png';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import SettingsIcon from '@mui/icons-material/Settings';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import StorageIcon from '@mui/icons-material/Storage';
import TuneIcon from '@mui/icons-material/Tune';
import { userAuthentication } from '../../../redux/UserInfo/isLoggedActions';
import { Dispatch } from 'redux';
import FetchData from '../../../Components/ServerCall/FetchData';
import {isLoggedProps} from "../../../redux/UserInfo/IsLoggedInterfaces";

const validateEmail = (email: string): boolean => {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
};

const validatePassword = (password: string): boolean => {
    return password.length >= 8;
};

type LoggedDetailsType = {
    isUserLogged: boolean;
    accessToken: string;
    refreshToken: string;
    tokenType: string;
    user: any;
};

const Login = (props: any) => {

    const navigate = useNavigate();
    const [account, setAccount] = useState({
        email: '',
        password: '',
        emailError: '',
        passwordError: '',
    });
    const [loginStatus, setLoginStatus] = useState(false);
    const [loginError, setLoginError] = useState(false);
    const [serverErrorMessage, setServerErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const resetEmailError = () => {
        setAccount({
            ...account,
            emailError: "",
        });
        setLoginError(false);
    };
    const resetPwdError = () => {
        setAccount({
            ...account,
            passwordError: "",
        });
    };
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoading(true);

        var canLogin = false;

        if (
            account.email.length > 0 &&
            account.password.length > 0 &&
            account.emailError === "" &&
            account.passwordError === ""
        ) {
            canLogin = true;
        }

        if (canLogin) {
            let form = {
                email: account.email,
                password: account.password,
                device: "web",
            };

            try {
                const response: any = await FetchData({
                    requestType: "auth",
                    method: "POST",
                    url: "login",
                    data: form,
                    headers: { "Content-Type": "application/json" }
                });

                if (response.status) {
                    setLoginStatus(true);
                    var payload = {
                        isUserLogged: true,
                        accessToken: response.data.token.access_token,
                        refreshToken: response.data.token.refresh_token,
                        tokenType: response.data.token.token_type,
                        user: response.data.user
                    };
                    props.userAuthentication(payload);
                    setLoading(false);
                    setTimeout(() => {
                        navigate("/datahome");
                    }, 1000);
                } else {
                    setLoginError(true);
                    setServerErrorMessage("Email or password is incorrect");
                    setLoading(false);
                }
            } catch (error) {
                setLoginError(true);
                setServerErrorMessage("Error connecting to server");
                setLoading(false);
            }

        } else {
            setLoginError(true);
            setServerErrorMessage("Provide valid credentials");
            setLoading(false);
        }
    };
    useEffect(() => {
        if(props.token){navigate("/dataHome");}
    }, []);
    return (
        <Box sx={{
            display: 'flex',
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: '-50%',
                right: '-50%',
                width: '100%',
                height: '100%',
                background: 'radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%)',
                animation: 'pulse 15s ease-in-out infinite',
            },
            '@keyframes pulse': {
                '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                '50%': { transform: 'translate(-10%, 10%) scale(1.1)' },
            }
        }}>
            {/* Logo in top left */}
            <Box sx={{ 
                position: 'absolute', 
                top: 24, 
                left: 24,
                zIndex: 10
            }}>
                <img src={logo} alt="Logo" style={{ height: '48px' }} />
            </Box>

            {/* Main Content Container */}
            <Grid container sx={{ 
                width: '100%',
                m: 'auto',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                position: 'relative',
                zIndex: 1
            }}>
                <Grid item xs={12} md={11} lg={10} xl={9}>
                    <Paper elevation={24} sx={{ 
                        display: 'flex', 
                        borderRadius: 4,
                        overflow: 'hidden',
                        minHeight: { xs: '600px', md: '650px' },
                        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                        '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: '0 25px 70px rgba(0,0,0,0.2)',
                        }
                    }}>
                            {/* Left Side - Login Form */}
                            <Box 
                                flex={1} 
                                display="flex" 
                                flexDirection="column" 
                                justifyContent="center" 
                                sx={{
                                    p: { xs: 4, md: 6 },
                                    bgcolor: 'white',
                                    position: 'relative'
                                }}
                            >
                                {/* Decorative elements */}
                                <Box sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '4px',
                                    background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
                                }} />

                                <Box sx={{ maxWidth: '420px', mx: 'auto', width: '100%' }}>
                                    <Typography 
                                        variant="h3" 
                                        align="center" 
                                        sx={{ 
                                            fontWeight: 700, 
                                            mb: 1,
                                            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text',
                                        }}
                                    >
                                        Welcome Back
                                    </Typography>
                                    <Typography 
                                        variant="body1" 
                                        align="center" 
                                        color="text.secondary" 
                                        sx={{ mb: 4, fontSize: '0.95rem' }}
                                    >
                                        Sign in to continue to your Pandop workspace
                                    </Typography>
                                <form onSubmit={handleSubmit}>
                                    <TextField
                                        label="Email Address"
                                        type="email"
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                        value={account.email}
                                        onChange={(e) => setAccount({...account, email: e.target.value})}
                                        onFocus={resetEmailError}
                                        onBlur={() => {
                                            setLoginError(false);
                                            var valid = validateEmail(account.email);
                                            if (valid) {
                                                setAccount({...account, emailError: ""});
                                            } else {
                                                setAccount({
                                                    ...account,
                                                    emailError: "Enter valid email address",
                                                });
                                            }
                                        }}
                                        error={Boolean(account.emailError)}
                                        helperText={account.emailError}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: '#f8fafc',
                                                transition: 'all 0.3s',
                                                '&:hover': {
                                                    backgroundColor: '#f1f5f9',
                                                },
                                                '&.Mui-focused': {
                                                    backgroundColor: 'white',
                                                    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                                                }
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <EmailIcon sx={{ color: '#64748b' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        label="Password"
                                        type="password"
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                        value={account.password}
                                        onChange={(e) => setAccount({...account, password: e.target.value})}
                                        onFocus={resetPwdError}
                                        onBlur={() => {
                                            setLoginError(false);
                                            var valid = validatePassword(account.password);
                                            if (valid) {
                                                setAccount({...account, passwordError: ""});
                                            } else {
                                                setAccount({
                                                    ...account,
                                                    passwordError: "Minimum 8 characters",
                                                });
                                            }
                                        }}
                                        error={Boolean(account.passwordError)}
                                        helperText={account.passwordError}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: '#f8fafc',
                                                transition: 'all 0.3s',
                                                '&:hover': {
                                                    backgroundColor: '#f1f5f9',
                                                },
                                                '&.Mui-focused': {
                                                    backgroundColor: 'white',
                                                    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                                                }
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LockIcon sx={{ color: '#64748b' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                                        <Box /> {/* Spacer */}
                                        <Link 
                                            href="#" 
                                            variant="body2"
                                            sx={{
                                                color: '#3b82f6',
                                                textDecoration: 'none',
                                                fontWeight: 500,
                                                '&:hover': {
                                                    textDecoration: 'underline',
                                                }
                                            }}
                                        >
                                            Forgot Password?
                                        </Link>
                                    </Box>
                                    
                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        disabled={loading}
                                        sx={{
                                            mt: 4,
                                            height: '56px',
                                            borderRadius: 2,
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
                                                boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
                                                transform: 'translateY(-2px)',
                                            },
                                            '&:active': {
                                                transform: 'translateY(0)',
                                            },
                                            '&:disabled': {
                                                background: '#e2e8f0',
                                                boxShadow: 'none',
                                            }
                                        }}
                                    >
                                        {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Sign In'}
                                    </Button>
                                    
                                    {loginError && (
                                        <Box sx={{ 
                                            mt: 2, 
                                            p: 2, 
                                            bgcolor: '#fef2f2', 
                                            borderRadius: 2,
                                            border: '1px solid #fecaca'
                                        }}>
                                            <Typography color="error" variant="body2" align="center" sx={{ fontWeight: 500 }}>
                                                {serverErrorMessage}
                                            </Typography>
                                        </Box>
                                    )}
                                    {loginStatus && (
                                        <Box sx={{ 
                                            mt: 2, 
                                            p: 2, 
                                            bgcolor: '#f0fdf4', 
                                            borderRadius: 2,
                                            border: '1px solid #bbf7d0'
                                        }}>
                                            <Typography color="success.main" variant="body2" align="center" sx={{ fontWeight: 500 }}>
                                                ✓ Logged in successfully! Redirecting...
                                            </Typography>
                                        </Box>
                                    )}
                                    {/*<Box mt={2} display="flex" justifyContent="center">*/}
                                    {/*    <Typography variant="body2" style={{fontSize: "11px"}}>*/}
                                    {/*        No account yet?&nbsp;*/}
                                    {/*        <Link*/}
                                    {/*            onClick={() => navigate("/signup")}*/}
                                    {/*            variant="body2"*/}
                                    {/*            style={{color: 'blue', cursor: 'pointer'}}*/}
                                    {/*        >*/}
                                    {/*            Register*/}
                                    {/*        </Link>*/}
                                    {/*    </Typography>*/}
                                    {/*</Box>*/}
                                </form>
                                </Box>
                            </Box>

                            {/* Right Side - Information */}
                            <Box 
                                flex={1} 
                                display="flex" 
                                flexDirection="column" 
                                justifyContent="center" 
                                sx={{
                                    p: { xs: 4, md: 6 },
                                    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                                    color: 'white',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: '-50%',
                                        right: '-50%',
                                        width: '200%',
                                        height: '200%',
                                        background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                                        animation: 'rotate 20s linear infinite',
                                    },
                                    '@keyframes rotate': {
                                        '0%': { transform: 'rotate(0deg)' },
                                        '100%': { transform: 'rotate(360deg)' },
                                    }
                                }}
                            >
                                <Box sx={{ position: 'relative', zIndex: 1 }}>
                                    <Typography 
                                        variant="h2" 
                                        gutterBottom 
                                        sx={{ 
                                            fontWeight: 800, 
                                            mb: 1,
                                            fontSize: { xs: '2.5rem', md: '3.5rem' },
                                            letterSpacing: '-0.02em',
                                        }}
                                    >
                                        Pandop
                                    </Typography>
                                    <Typography 
                                        variant="h6" 
                                        gutterBottom 
                                        sx={{ 
                                            fontWeight: 300, 
                                            mb: 5, 
                                            textTransform: 'uppercase', 
                                            letterSpacing: 3, 
                                            opacity: 0.9,
                                            fontSize: { xs: '0.9rem', md: '1rem' }
                                        }}
                                    >
                                        Process. Analytics. Data. Optimization.
                                    </Typography>
                                
                                    <Box>
                                        {[
                                            {
                                                icon: <SettingsIcon sx={{ fontSize: 32, color: 'white' }} />,
                                                title: 'Process (Engineering)',
                                                description: 'AI-driven insights for efficient, reliability, and safer water operations.'
                                            },
                                            {
                                                icon: <AnalyticsIcon sx={{ fontSize: 32, color: 'white' }} />,
                                                title: 'Analytics',
                                                description: 'Democratize ML—forecast demand, detect leaks, and optimize without code.'
                                            },
                                            {
                                                icon: <StorageIcon sx={{ fontSize: 32, color: 'white' }} />,
                                                title: 'Data',
                                                description: 'Unify and transform raw plant data into actionable intelligence.'
                                            },
                                            {
                                                icon: <TuneIcon sx={{ fontSize: 32, color: 'white' }} />,
                                                title: 'Optimization',
                                                description: 'Cut costs and energy use while boosting network performance and resilience.'
                                            }
                                        ].map((feature, index) => (
                                            <Box 
                                                key={index}
                                                display="flex" 
                                                alignItems="center" 
                                                mb={3}
                                                sx={{
                                                    transition: 'transform 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'translateX(10px)',
                                                    }
                                                }}
                                            >
                                                <Box sx={{ 
                                                    width: 56, 
                                                    height: 56, 
                                                    borderRadius: 2, 
                                                    bgcolor: 'rgba(255,255,255,0.15)', 
                                                    backdropFilter: 'blur(10px)',
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    mr: 2.5,
                                                    flexShrink: 0,
                                                    border: '1px solid rgba(255,255,255,0.2)',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                }}>
                                                    {feature.icon}
                                                </Box>
                                                <Box>
                                                    <Typography 
                                                        variant="h6" 
                                                        sx={{ 
                                                            fontWeight: 600, 
                                                            mb: 0.5,
                                                            fontSize: '1.05rem' 
                                                        }}
                                                    >
                                                        {feature.title}
                                                    </Typography>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            opacity: 0.9, 
                                                            lineHeight: 1.6,
                                                            fontSize: '0.9rem'
                                                        }}
                                                    >
                                                        {feature.description}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
        </Box>
    );
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
    return {
        userAuthentication: (payload: LoggedDetailsType) => dispatch(userAuthentication(payload)),
    };
};

const mapStateToProps = (state: isLoggedProps, ownProps: any) => {
    return {
        token: state.isLogged.accessToken,
    };
};


export default connect(mapStateToProps, mapDispatchToProps)(Login);
